import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Users, Trash2, Send, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * Panel admin de grupos Telegram (embed en cualquier página admin).
 * Muestra grupos registrados, permite (des)activar, borrar y hacer broadcast.
 */
export default function TelegramGroupsAdmin() {
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [broadcast, setBroadcast] = useState({ tipo: "montadores", text: "" });
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telegram/admin/groups");
      setItems(data.items || []);
      setTipos(data.tipos_validos || []);
    } catch (err) {
      toast.error("No pude cargar los grupos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (g) => {
    try {
      await api.post(`/telegram/admin/groups/${g.id}/toggle`, { activo: !g.activo });
      load();
    } catch (err) {
      toast.error("Error");
    }
  };

  const eliminar = async (g) => {
    if (!confirm(`¿Eliminar el grupo "${g.chat_title}" (${g.tipo})?`)) return;
    try {
      await api.delete(`/telegram/admin/groups/${g.id}`);
      toast.success("Grupo eliminado");
      load();
    } catch (err) {
      toast.error("Error");
    }
  };

  const enviarBroadcast = async () => {
    if (!broadcast.text.trim()) {
      toast.error("Escribe el mensaje");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/telegram/admin/groups/broadcast", broadcast);
      toast.success(`Enviado a ${data.enviados}/${data.total} grupo(s)`);
      setBroadcast({ ...broadcast, text: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-testid="tg-groups-admin">
      <div className="flex items-center gap-2 text-sky-300 font-semibold mb-3">
        <Users className="w-4 h-4" /> Grupos Telegram · broadcast
      </div>

      {items.length === 0 && !loading && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200 mb-3 flex gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Ningún grupo registrado aún</div>
            <div>Para registrar uno: añade <b>@RotulacionDNF_bot</b> como admin al grupo de Telegram, y desde el grupo escribe:</div>
            <code className="bg-zinc-900 px-2 py-0.5 rounded text-sky-200 mt-1 inline-block">/registrar_grupo TIPO</code>
            <div className="mt-1">Tipos: {tipos.join(", ")}</div>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2 mb-4">
          {items.map((g) => (
            <div key={g.id} className="flex items-center gap-3 bg-zinc-950/60 border border-zinc-800 rounded p-2"
                 data-testid={`tg-group-${g.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">
                  {g.chat_title || <i className="text-zinc-500">sin título</i>}
                </div>
                <div className="text-xs text-zinc-500 font-mono">
                  {g.tipo} · chat_id {g.chat_id}
                </div>
              </div>
              <Switch checked={!!g.activo} onCheckedChange={() => toggle(g)} data-testid={`tg-group-toggle-${g.id}`} />
              <Button variant="ghost" size="sm" onClick={() => eliminar(g)}
                className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                data-testid={`tg-group-del-${g.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="pt-3 border-t border-zinc-800">
        <div className="text-zinc-300 text-xs uppercase tracking-wider mb-2">Broadcast</div>
        <div className="grid grid-cols-4 gap-2 mb-2">
          <select
            value={broadcast.tipo}
            onChange={(e) => setBroadcast({ ...broadcast, tipo: e.target.value })}
            className="col-span-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-2 text-sm text-zinc-100"
            data-testid="tg-broadcast-tipo">
            {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="col-span-3">
            <Textarea
              value={broadcast.text}
              onChange={(e) => setBroadcast({ ...broadcast, text: e.target.value })}
              rows={3}
              placeholder="Ej: Mañana no hay ruta, reunión a las 9h en nave."
              className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm"
              data-testid="tg-broadcast-text"
            />
          </div>
        </div>
        <Button onClick={enviarBroadcast} disabled={sending || !broadcast.text.trim()}
          className="bg-sky-500 hover:bg-sky-400 text-zinc-900 font-semibold"
          data-testid="tg-broadcast-send">
          {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</> : <><Send className="w-4 h-4 mr-2" /> Enviar al grupo</>}
        </Button>
        <p className="text-[10px] text-zinc-500 mt-2">
          Se envía a TODOS los grupos activos del tipo elegido, con firma automática.
        </p>
      </div>
    </div>
  );
}
